/**
 * format.ts — every number the client shows a player goes through here.
 *
 * Moved from components/game/common/financial/ so domains and selectors can
 * use it too, and made locale-aware: digits and separators follow the language
 * the player chose (`1,234.5` in English, `1.234,5` in Portuguese), where the
 * old `toFixed` / bare `toLocaleString()` calls followed nothing or the
 * browser. design-checks' `bare-locale-format` rule holds bare calls at zero.
 *
 * House style for money is the game's own, not Intl's currency style: a `$`
 * prefix and a K/M/B suffix in every language, always one decimal, and a loss
 * signed before the `$` — `-$52.9M`. Intl would move the symbol (`1234,50 US$`
 * in Spanish, `US$ 1.234,50` in Portuguese), and the split-flap boards the
 * redesign draws numbers on need one shape across locales.
 */

import i18n from 'i18next';

const SIGNED_ZERO_THRESHOLD = 1e-6;
/**
 * Placeholder shown wherever a figure has no value yet. Exported so callers can
 * detect a placeholder and style it as deliberately blank rather than let it
 * read as a broken number.
 */
export const EM_DASH = '—';

// ─────────────────────────────────────────────────────────────────────────────
// Locale
// ─────────────────────────────────────────────────────────────────────────────

/** The language the player chose, as a BCP 47 tag — English until i18n has started. */
function formatLocale(): string {
  return i18n.resolvedLanguage || i18n.language || 'en';
}

const numberFormats = new Map<string, Intl.NumberFormat>();

/**
 * A number with a fixed count of decimals, grouped and punctuated for the
 * player's language. Formatters are cached per locale and precision, since
 * tables re-render them constantly.
 */
export function formatNumber(value: number, fractionDigits = 0): string {
  const locale = formatLocale();
  const key = `${locale}|${fractionDigits}`;
  let format = numberFormats.get(key);
  if (!format) {
    format = new Intl.NumberFormat(locale, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });
    numberFormats.set(key, format);
  }
  return format.format(value);
}

const decimalFormats = new Map<string, Intl.NumberFormat>();

/**
 * A number as a player types it: the inverse of `formatNumber`, so `1,234.5`
 * in English and `1.234,5` in Spanish and Portuguese both read 1234.5.
 * Whichever of `.` and `,` comes last is the decimal mark when one or two
 * digits follow it, so `2.5` and `2,5` both read 2.5 in every language; with
 * three after it, the language decides — its own thousands mark groups
 * (`1.234` is 1234 in Portuguese), anything else is a decimal mark. NaN for
 * anything that is not a number, and for nothing at all.
 */
export function readNumber(text: string): number {
  const compact = text.replace(/\s/g, '');
  if (!/^-?[\d.,]*\d[\d.,]*$/.test(compact)) return Number.NaN;
  const last = Math.max(compact.lastIndexOf('.'), compact.lastIndexOf(','));
  if (last < 0) return Number(compact);
  const after = compact.slice(last + 1);
  const before = compact.slice(0, last).replace(/[.,]/g, '');
  const decimalMark = new Intl.NumberFormat(formatLocale()).formatToParts(1.5).find((part) => part.type === 'decimal')?.value;
  const decimal = after.length !== 3 || compact[last] === decimalMark;
  return Number(decimal ? `${before}.${after}` : `${before}${after}`);
}

/**
 * A number as `toLocaleString()` would print it — grouped, up to three
 * decimals — but in the player's language rather than the browser's. The
 * locale-aware drop-in for the bare calls this file replaced.
 */
export function formatDecimal(value: number): string {
  const locale = formatLocale();
  let format = decimalFormats.get(locale);
  if (!format) {
    format = new Intl.NumberFormat(locale);
    decimalFormats.set(locale, format);
  }
  return format.format(value);
}

/** A date and time in the player's language. */
export function formatDateTime(date: Date): string {
  return date.toLocaleString(formatLocale());
}

/** The time of day alone, to the second, in the player's language: "2:02:31 PM", "14:02:31". */
export function formatClockTime(date: Date): string {
  return date.toLocaleTimeString(formatLocale());
}

/** A day of the year, its month in words, in the player's language: "October 3", "3 de octubre", "3 de outubro". */
export function formatDayMonth(date: Date): string {
  return date.toLocaleDateString(formatLocale(), { day: 'numeric', month: 'long' });
}

/** The time of day to the minute, as the player's language writes it short: "7:35 PM", "19:35", "08:00". */
export function formatTimeOfDay(date: Date): string {
  return date.toLocaleTimeString(formatLocale(), { timeStyle: 'short' });
}

/**
 * How long something took, in one shape: whole milliseconds under a second
 * ("86 ms"), seconds with one decimal from there ("1.2 s", "1,2 s"). The unit
 * is a symbol, the same in every language, a space before it — as the
 * connection's round trip is written — and apart, for a figure that sets it
 * smaller than the number.
 */
export function durationParts(ms: number): { figure: string; unit: 'ms' | 's' } {
  return ms < 1000 ? { figure: formatNumber(Math.round(ms)), unit: 'ms' } : { figure: formatNumber(ms / 1000, 1), unit: 's' };
}

/** A duration as `durationParts` shapes it, in one string: "86 ms", "1.2 s". */
export function formatDuration(ms: number): string {
  const { figure, unit } = durationParts(ms);
  return `${figure} ${unit}`;
}

/**
 * A countdown, as minutes and seconds: `0:36`, `14:22`, `90:00`. Minutes run
 * past sixty rather than rolling into hours, as a session's clock does; a
 * fraction of a second rounds down, so it never shows time that has gone.
 * English, Spanish and Portuguese all write a clock this way.
 */
export function formatCountdown(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
}

/**
 * People's names in a line, joined as the player's language joins a list:
 * "Leo", "Leo and Mia", "Leo, Mia and Ana" — "y" in Spanish, "e" in
 * Portuguese. The names come as the game gives them.
 */
export function formatNameList(names: readonly string[]): string {
  return new Intl.ListFormat(formatLocale(), { style: 'long', type: 'conjunction' }).format(names);
}

/**
 * A GMT offset as a player's account holds it, signed for showing after
 * "GMT": "+3" → "+3", "-5" → "−5" (a true minus), "5:30" → "+5:30". Empty
 * when the account holds none, or nothing that reads as an offset, so the
 * caller can leave "GMT" out too.
 */
export function formatGmtOffset(offset: string): string {
  const text = offset.trim();
  if (!/^[+-]?\d{1,2}(:\d{2})?$/.test(text)) return '';
  return (/^[+-]/.test(text) ? text : `+${text}`).replace('-', '−');
}

/**
 * Normalizes a value, treating near-zero values as zero and invalid values as undefined.
 */
export function normalizeSignedValue(value: number | undefined): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return undefined;
  }
  if (Math.abs(value) < SIGNED_ZERO_THRESHOLD) {
    return 0;
  }
  return value;
}

/**
 * A negative figure's sign, written before any `$`: the hyphen-minus the
 * language's own number format writes, so money and every other figure agree.
 */
const MINUS = '-';

const UNITS = [
  [1e9, 'B'],
  [1e6, 'M'],
  [1e3, 'K'],
] as const;

/**
 * A magnitude on the K/M/B ladder: the figure scaled, and its unit ('' below
 * a thousand). A figure takes the first unit it reaches, or would reach once
 * rounded to `digits` decimals: 999,960 rounds to 1,000.0K, so it is 1.0M.
 */
function magnitude(abs: number, digits = 1): [number, '' | 'K' | 'M' | 'B'] {
  const rounded = (value: number) => Math.round(value * 10 ** digits) / 10 ** digits;
  for (const [size, unit] of UNITS) {
    if (abs >= size || rounded(abs / (size / 1000)) >= 1000) return [abs / size, unit];
  }
  return [abs, ''];
}

/**
 * Formats a number compactly with K/M/B suffixes, one decimal on a scaled one.
 */
function formatCompact(value: number): string {
  const sign = value < 0 ? MINUS : '';
  const [scaled, unit] = magnitude(Math.abs(value));
  return `${sign}${formatNumber(scaled, unit ? 1 : 0)}${unit}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Cash Formatters
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Money held in millions, the game's one shape for it: `$5.5M`, a loss
 * `-$52.9M` — the sign before the `$` — always to one decimal.
 */
export function formatCash(millions: number): string {
  const value = normalizeSignedValue(millions) ?? 0;
  return `${value < 0 ? MINUS : ''}$${formatNumber(Math.abs(value), 1)}M`;
}

export function formatMaybeCash(value: number | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return EM_DASH;
  }
  return formatCash(value);
}

export function formatMaybeCashZeroDash(value: number | undefined): string {
  const normalized = normalizeSignedValue(value);
  if (typeof normalized !== 'number') return EM_DASH;
  if (normalized === 0) return EM_DASH;
  return formatCash(normalized);
}

export function formatCashCompactZeroDash(value: number | undefined): string {
  const normalized = normalizeSignedValue(value);
  if (typeof normalized !== 'number') return EM_DASH;
  if (normalized === 0) return EM_DASH;
  if (Math.abs(normalized) < 0.1) {
    return `${normalized < 0 ? MINUS : ''}$${formatNumber(Math.abs(normalized) * 1000, 1)}K`;
  }
  return formatCash(normalized);
}

/**
 * Money held in millions with its sign always shown, for a gain or a loss:
 * `+$4.1M`, `-$52.9M`, and `+$50.0K` below a tenth of a million. A zero has no
 * sign to show: it prints a dash, or `$0.0M` where a figure is wanted
 * (`zero: 'figure'` — a board's profit, a sentence's).
 */
export function formatCashSigned(value: number | undefined, { zero = 'dash' }: { zero?: 'dash' | 'figure' } = {}): string {
  const normalized = normalizeSignedValue(value);
  if (typeof normalized !== 'number') return EM_DASH;
  if (normalized === 0) return zero === 'figure' ? formatCash(0) : EM_DASH;
  const sign = normalized >= 0 ? '+' : MINUS;
  const absValue = Math.abs(normalized);
  if (absValue < 0.1) {
    return `${sign}$${formatNumber(absValue * 1000, 1)}K`;
  }
  return `${sign}${formatCash(absValue)}`;
}

/**
 * Money held in plain dollars rather than millions — a route's revenue or
 * cost a day, which the server keeps on each segment in dollars — on the
 * K/M/B ladder, to one decimal as all money is: `$407.1K`, `$9.8K`, `$1.2M`,
 * `$950.0`. A loss reads `-$20.5K`, the sign before the `$`.
 */
export function formatDollarsCompact(dollars: number): string {
  const value = normalizeSignedValue(dollars) ?? 0;
  const [scaled, unit] = magnitude(Math.abs(value));
  return `${value < 0 ? MINUS : ''}$${formatNumber(scaled, 1)}${unit}`;
}

/**
 * A gain or a loss in plain dollars, its sign always shown: a route's profit
 * a day beside its revenue and cost — `+$153.2K`, `-$20.5K`. A zero has no
 * sign to show: `$0.0`.
 */
export function formatDollarsCompactSigned(dollars: number): string {
  const value = normalizeSignedValue(dollars) ?? 0;
  return value > 0 ? `+${formatDollarsCompact(value)}` : formatDollarsCompact(value);
}

/**
 * A fare, in whole dollars as a ticket prints it: `$1,650`, `$38`. Fares are
 * held in plain dollars; a cent is never worth showing on one.
 */
export function formatFare(dollars: number): string {
  const value = normalizeSignedValue(dollars) ?? 0;
  return `${value < 0 ? MINUS : ''}$${formatNumber(Math.abs(value))}`;
}

/**
 * The price of one unit of something, in dollars and cents — jet fuel by the
 * litre, a landing fee by the tonne — which the city data holds in plain
 * dollars: `$0.78`, `$16.09`. The unit is the caller's to set beside it.
 */
export function formatUnitPrice(dollars: number): string {
  const value = normalizeSignedValue(dollars) ?? 0;
  return `${value < 0 ? MINUS : ''}$${formatNumber(Math.abs(value), 2)}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Percent Formatters
// ─────────────────────────────────────────────────────────────────────────────

/** A fraction as a percentage, to one decimal unless told otherwise: `87.3%`. */
export function formatPercent(value: number, fractionDigits = 1): string {
  return `${formatNumber(value * 100, fractionDigits)}%`;
}

/**
 * A part of a whole, held 0 to 1 — a cost line's share of the quarter's
 * operating costs — as the Financials board prints it: whole from 10%
 * (`87%`), one decimal under it so a sliver is not rounded away (`4.2%`), and
 * none as `0%`.
 */
export function formatShare(share: number): string {
  const percent = share * 100;
  return `${formatNumber(percent, percent > 0 && percent < 10 ? 1 : 0)}%`;
}

/**
 * How many of a list are yours, of all of it — a pair's flights while only
 * yours show, a city's destinations — in the one spelling a count of a count
 * has, the decision button's: `2 / 4`.
 */
export function formatCountOf(part: number, whole: number): string {
  return `${formatDecimal(part)} / ${formatDecimal(whole)}`;
}

export function formatMaybePercent(value: number | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return EM_DASH;
  }
  return formatPercent(value);
}

/**
 * A margin — a share of revenue, held as a fraction — as the statements print
 * it: one decimal while it is under a hundred percent (`10.7%`), none past it
 * (`-2,332%`), a loss signed as every figure is.
 */
export function formatMargin(fraction: number): string {
  const percent = (normalizeSignedValue(fraction) ?? 0) * 100;
  const magnitude = Math.abs(percent);
  return `${percent < 0 ? MINUS : ''}${formatNumber(magnitude, magnitude >= 100 ? 0 : 1)}%`;
}

/**
 * A change on the figure before it — a year's revenue on the year before's —
 * held as a fraction, and signed both ways: `+12.4%`, `-3.0%`; past a hundred
 * percent without a decimal, as a margin is (`+250%`). No change is `0.0%`.
 */
export function formatChange(fraction: number): string {
  const percent = (normalizeSignedValue(fraction) ?? 0) * 100;
  const magnitude = Math.abs(percent);
  const sign = percent < 0 ? MINUS : percent > 0 ? '+' : '';
  return `${sign}${formatNumber(magnitude, magnitude >= 100 ? 0 : 1)}%`;
}

export function formatOwnershipPercent(value: number | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return EM_DASH;
  }
  return `${formatNumber(value, Number.isInteger(value) ? 0 : 1)}%`;
}

/**
 * A percentage already in percent — a margin, a load factor — at
 * `fractionDigits` decimals: `12.6%`, `-6.8%`.
 * `signed` puts a plus on a gain: `+33%`. A figure that rounds to nothing keeps
 * its sign, so a route losing a little still reads as losing: `-0%`.
 */
export function formatPercentFigure(percent: number, fractionDigits = 0, signed = false): string {
  const value = normalizeSignedValue(percent) ?? 0;
  const sign = value < 0 ? MINUS : signed && value > 0 ? '+' : '';
  return `${sign}${formatNumber(Math.abs(value), fractionDigits)}%`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Number/Count Formatters
// ─────────────────────────────────────────────────────────────────────────────

/** A count, compact — `612`, `48.2K`, `1.2B` — where the exact figure would not fit. */
export function formatCountCompact(value: number): string {
  return formatCompact(value);
}

export function formatCountCompactZeroDash(value: number | undefined): string {
  const normalized = normalizeSignedValue(value);
  if (typeof normalized !== 'number') return EM_DASH;
  if (normalized === 0) return EM_DASH;
  return formatCompact(normalized);
}

// ─────────────────────────────────────────────────────────────────────────────
// Axis ticks
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A count as an axis tick: `0`, `750`, `48K`, `1.5M` — with no decimal a
 * round tick does not need, since a count is not money.
 */
export function formatCountTick(value: number): string {
  const count = normalizeSignedValue(value) ?? 0;
  const [scaled, unit] = magnitude(Math.abs(count));
  return `${count < 0 ? MINUS : ''}${formatDecimal(scaled)}${unit}`;
}

/**
 * Money held in millions as an axis tick, in the shape all money takes — one
 * decimal, the sign before the `$` — on the K/M/B ladder: `$250.0K`, `$2.5M`,
 * `-$60.0M`, `$1.5B`; and `$0.0M` at zero, as `formatCash` writes it.
 */
export function formatCashTick(millions: number): string {
  const cash = normalizeSignedValue(millions) ?? 0;
  if (cash === 0) return formatCash(0);
  return formatDollarsCompact(cash * 1_000_000);
}

/**
 * A percentage — already in percent, 0 to 100 — as an axis tick: `0%`,
 * `0.2%`, `-4.5%`. Ticks near zero fall a fifth of a point apart, so the tick
 * keeps the decimals it has rather than rounding two of them to one label.
 */
export function formatPercentTick(percent: number): string {
  return `${formatDecimal(normalizeSignedValue(percent) ?? 0)}%`;
}

/**
 * A ratio as `0.42x`, a dash while there is none. A zero is a figure — a team
 * with no debt has a debt:equity of 0.00x — not a missing one.
 */
export function formatMaybeRatio(value: number | undefined): string {
  const normalized = normalizeSignedValue(value);
  if (typeof normalized !== 'number') return EM_DASH;
  return `${formatNumber(normalized, 2)}x`;
}

/**
 * Revenue or cost per available seat-km, as the server sends it — in cents —
 * with the cent sign: `6.1¢`. A dash while there is no figure.
 */
export function formatCentsPerSeatKm(cents: number | undefined): string {
  const normalized = normalizeSignedValue(cents);
  if (typeof normalized !== 'number' || normalized === 0) return EM_DASH;
  return `${formatNumber(normalized, 1)}\u00a2`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Split-flap parts
// ─────────────────────────────────────────────────────────────────────────────

/** One character of a number as a split-flap board draws it. */
export interface FlapPart {
  char: string;
  /** `digit` gets a tile · `unit` a fixed, dimmed character beside the tiles · `separator` no tile at all. */
  kind: 'digit' | 'unit' | 'separator';
}

/**
 * A figure split for the split-flap tiles, as FlapNumber takes it. No tile
 * carries a sign: a figure on the flaps reads as positive, and one below zero
 * says so in its colour — FlapNumber draws a `negative` figure's digits in the
 * board's coral. Only `flapParts`, `flapCountParts` and `flapBlank` make one:
 * the class is exported as a type alone, and its private fields make that
 * type nominal, so tiles listed by hand, a sign among them, are not a
 * FlapParts, and neither is a copy spread from one, which leaves the fields
 * behind.
 */
class FlapParts {
  readonly #characters: readonly FlapPart[];
  readonly #negative: boolean;
  readonly #text: string;

  constructor(characters: readonly FlapPart[], negative: boolean, text: string) {
    this.#characters = characters;
    this.#negative = negative;
    this.#text = text;
  }

  /** The figure's characters, its sign taken off: digits on tiles, units and separators beside them. */
  get characters(): readonly FlapPart[] {
    return this.#characters;
  }

  /**
   * How many tiles the figure turns: its digits, the only characters on a
   * tile. What a board counts to choose a size its figures fit at.
   */
  get digits(): number {
    return this.#characters.filter((part) => part.kind === 'digit').length;
  }

  /** The figure is below zero: what its sign said, which its colour says now. */
  get negative(): boolean {
    return this.#negative;
  }

  /** The figure as written, its sign kept: what is read out for the tiles. */
  get text(): string {
    return this.#text;
  }
}

export type { FlapParts };

/** The house style's symbols and suffixes — every one the formatters above emit: beside the tiles, dimmed, never on one. */
const FLAP_UNITS = new Set(['$', '\u00a2', 'K', 'M', 'B', '%', 'x']);

/**
 * Grouping and decimal marks in every language the game speaks, plus the
 * spaces some locales group with (a no-break space, a narrow no-break space)
 * and the `/` of "1/2".
 */
const FLAP_SEPARATORS = new Set(['.', ',', ':', '/', ' ', '\u00a0', '\u202f']);

/** The signs a figure is written with: a gain's plus, and a loss's minus — the hyphen the formatters write, or a true minus. */
const FLAP_SIGNS = new Set(['+', MINUS, '\u2212']);

/**
 * A formatted number, split for a split-flap board. Takes the output of the
 * formatters above rather than a number, so the board shows exactly what the
 * rest of the screen does — `$12,345.6M` in English, `$12.345,6M` in
 * Portuguese — and only decides which characters get a tile: the digits.
 * Its sign gets nothing: `-$52.9M` is the characters of `$52.9M`, negative,
 * and `+$4.1M` those of `$4.1M`.
 */
export function flapParts(text: string): FlapParts {
  const sign = FLAP_SIGNS.has(text[0]) ? text[0] : '';
  const characters = [...text.slice(sign.length)].map<FlapPart>((char) => ({
    char,
    kind: FLAP_UNITS.has(char) ? 'unit' : FLAP_SEPARATORS.has(char) ? 'separator' : 'digit',
  }));
  return new FlapParts(characters, sign !== '' && sign !== '+', text);
}

/**
 * One flap tile at rest, for a figure not in yet. An em dash on a tile would
 * sit on the hinge and vanish.
 */
export function flapBlank(): FlapParts {
  const blank = '\u2007';
  return new FlapParts([...blank].map((char) => ({ char, kind: 'digit' })), false, blank);
}

/**
 * A count on flap tiles, two of them at least — "02 idle", "17 in the fleet"
 * — as the boards draw counts. A count of 100 or more takes the tiles it needs.
 */
export function flapCountParts(count: number): FlapParts {
  return flapParts(formatNumber(count).padStart(2, '0'));
}

// ─────────────────────────────────────────────────────────────────────────────
// Composite Formatters
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A figure as a statement line prints it: `$220.0M`, and with a margin
 * `-$52.9M (-2,332%)` or `$4.1M (10.7%)` — money as `formatCash` writes it,
 * the margin by `formatMargin`.
 * A dash for a zero or a figure that is not there.
 */
export function formatStatementCash(
  cashValue: number | undefined,
  marginValue?: number
): string {
  const normalizedCash = normalizeSignedValue(cashValue);
  if (typeof normalizedCash !== 'number') return EM_DASH;
  if (normalizedCash === 0) return EM_DASH;
  const cashStr = formatCash(normalizedCash);
  const normalizedMargin = normalizeSignedValue(marginValue);
  if (typeof normalizedMargin !== 'number') {
    return cashStr;
  }
  return `${cashStr} (${formatMargin(normalizedMargin)})`;
}
